import React, { useEffect, useState } from "react";
import * as style from "./AllAudits.module.scss";
import { TransactionDetailsGrid } from "cvmgmt-txn-history-ui-lib";
import CustomViewsMenu from "./CustomViewsMenu";
import { getEnv } from "../../../Constants/env";
import { useSelector } from "react-redux";
import { useAuthSession } from "../../../hooks/useAuthSession";

interface MenuItem {
  id: string;
  fullName: string;
  shortName: string;
  apiUrl: string;
}

const menuItems: MenuItem[] = [
  {
    id: "publish",
    fullName: "Publish",
    shortName: "PUB",
    apiUrl: "/audit/publish",
  },
  {
    id: "promote",
    fullName: "Promote",
    shortName: "PRO",
    apiUrl: "/audit/promote",
  },
  {
    id: "roles",
    fullName: "Roles",
    shortName: "ROL",
    apiUrl: "/audit/roles",
  },
  {
    id: "features",
    fullName: "Features",
    shortName: "FET",
    apiUrl: "/audit/features",
  },
  {
    id: "apis",
    fullName: "APIs",
    shortName: "API",
    apiUrl: "/audit/api-details",
  },
  {
    id: "applicationDetails",
    fullName: "Application Details",
    shortName: "APP",
    apiUrl: "/audit/application-details",
  },
  {
    id: "contexts",
    fullName: "Contexts",
    shortName: "CON",
    apiUrl: "/audit/contexts",
  },
  {
    id: "domains",
    fullName: "Domains",
    shortName: "DOM",
    apiUrl: "/audit/domains",
  },
  {
    id: "groups",
    fullName: "Groups",
    shortName: "GRP",
    apiUrl: "/audit/groups",
  },
  {
    id: "permissions",
    fullName: "Permissions",
    shortName: "PER",
    apiUrl: "/audit/permissions",
  },
  {
    id: "featureGroups",
    fullName: "Feature Groups",
    shortName: "FGP",
    apiUrl: "/audit/feature-groups",
  },
  {
    id: "valueStreams",
    fullName: "Value Streams",
    shortName: "VST",
    apiUrl: "/audit/value-streams",
  },
];

const AllAudits: React.FC = () => {
  const [isLeftSectionOpen, setIsLeftSectionOpen] = useState(true);
  const { env } = useSelector((state: any) => state?.createApplication);
  const authSession = useAuthSession();
  const idToken = authSession?.idToken ?? null;
  const loggedInUserRoles =
    authSession?.idTokenClaims?.roles ??
    authSession?.account?.idTokenClaims?.roles ??
    [];
  const scssClass = style as any;
  const [selectedItem, setSelectedItem] = useState("publish");

  const handleMenuItemClick = (itemId: string) => {
    setSelectedItem(itemId);
  };

  // Check if user is SuperAdmin (RoleAdmin)
  const isSuperAdmin = loggedInUserRoles.some((role: string) =>
    role.includes("RoleAdmin"),
  );

  // Check if user is AppAdmin
  const isAppAdmin = loggedInUserRoles.some((role: string) =>
    role.includes("AppAdmin"),
  );

  // Check if user is AppRoleApprover
  const isAppRoleApprover = loggedInUserRoles.some((role: string) =>
    role.includes("AppRoleApprover"),
  );

  // Check if user is ONLY an approver (has AppRoleApprover but not RoleAdmin or AppAdmin)
  const isOnlyApprover = isAppRoleApprover && !isSuperAdmin && !isAppAdmin;

  // Filter menu items based on user role
  const getFilteredMenuItems = () => {
    if (isSuperAdmin) {
      // SuperAdmin sees all items
      return menuItems;
    } else if (isAppAdmin) {
      // AppAdmin sees all except featureGroups and valueStreams
      return menuItems.filter(
        (item) => item.id !== "featureGroups" && item.id !== "valueStreams",
      );
    } else if (isOnlyApprover) {
      // Approver sees only publish and groups
      return menuItems.filter(
        (item) => item.id === "groups" || item.id === "publish",
      );
    }
    return [];
  };

  const filteredMenuItems = getFilteredMenuItems();

  // Update selected item if user doesn't have access to the current selection
  useEffect(() => {
    if (
      filteredMenuItems.length > 0 &&
      !filteredMenuItems.some((item) => item.id === selectedItem)
    ) {
      setSelectedItem(filteredMenuItems[0].id);
    }
  }, [filteredMenuItems, selectedItem]);

  const getTitle = () => {
    const item = menuItems.find((m) => m.id === selectedItem);
    return item?.fullName || "Activity";
  };

  // Build groups based on user role
  const getViewGroups = () => {
    const groups = [];

    if (isAppAdmin || isSuperAdmin || isOnlyApprover) {
      groups.push({
        title: "Workflow",
        items: filteredMenuItems.map((item) => ({
          id: item.id,
          label: item.fullName,
          shortLabel: item.shortName,
        })),
      });
    }

    return groups;
  };

  return (
    <div className={scssClass.pageContainer}>
      <CustomViewsMenu
        isPanelOpen={isLeftSectionOpen}
        groups={getViewGroups()}
        selectedItem={selectedItem}
        onItemClick={handleMenuItemClick}
        onPanelToggle={setIsLeftSectionOpen}
      />

      {/* Right Section */}
      <div className={scssClass.rightSection}>
        <div className={scssClass.rightHeader}>
          <p className={scssClass.breadcrumb}>Audit History · {getTitle()}</p>
        </div>
        <div className={scssClass.rightContent}>
          {idToken && (
            <TransactionDetailsGrid
              key={selectedItem}
              setTransactionInfo={() => {}}
              sortings={[{ id: "activity_date", desc: true }]}
              gridtitle={getTitle()}
              url={`${getEnv()?.baseURL}/audit/data?env=${env}`}
              payload={{
                valueType: getTitle(),
                sortingFilter: {
                  filter: [],
                  sort: [],
                  page: {
                    limit: 25,
                    offset: 0,
                  },
                },
              }}
              headers={{
                Authorization: idToken,
                "Content-Type": "application/json",
                Accept: "application/json",
              }}
              tableStyles={{ height: "calc(100vh - 29.5rem)" }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AllAudits;
